import Button from '../../components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckFront } from '@fortawesome/free-solid-svg-icons';
import styles from './styles.module.css';
import Input from '../../components/common/Input';
import { useEffect, useState } from 'react';
import { getCarsRequest } from '../../types/car';
import CheckBoxGroup, {
  CheckBoxGroupOnChangeProps,
  ValueObj,
} from '../../components/common/CheckBoxGroup';
import CheckBox from '../../components/common/CheckBox';
import Pagenation from '../../components/common/Pagenation';
import CarsTable from './components/CarsTable';
import {
  useGetCarCategoriesQuery,
  useGetCarsQuery,
  usePostCarsMutation,
} from '../../api/carApi';
import { useGetDriverLicensesQuery } from '../../api/driverApi';
import EditSideBar, {
  CarFormDataProvider,
  CarFormDataProviderProps,
} from './components/EditSideBar';
import PolicyRequired from '../../components/common/PolicyRequired';
import CsvDownload from '../../components/CsvDownload';
import CsvInput from '../../components/CsvInput';
import HeaderLayout from '../../components/HeaderLayout';
import SelectPageSize from '../../components/common/SelectPageSize';
import NoResultFound from '../../components/common/NoResultFound';
import { logger } from '../../services/logger';
import { keyList, useKeyAction } from '../../services/useKeyAction';

function Cars() {
  const initCars: Omit<getCarsRequest, 'page' | 'limit'> = {
    carCategoryIds: [],
    carNo: '',
    driverLicenseIds: [],
    isAvailable: ['available', 'unavailable'],
  };

  // ページャーなどの情報
  const [carsData, updateCarsData] = useState<getCarsRequest>({
    ...initCars,
    page: 1,
    limit: 20,
  });

  const { data: cars } = useGetCarsQuery(carsData);

  // 車両カテゴリ
  const { data: carCategories } = useGetCarCategoriesQuery();
  const [carCategoryCheckBoxGroupValues, updateCarCategoryCheckBoxGroupValues] =
    useState<ValueObj[]>([]);
  useEffect(() => {
    updateCarCategoryCheckBoxGroupValues(
      (carCategories || []).map((item) => ({
        value: `${item.carCategoryId}`,
        label: item.carCategory,
        checked: false,
      }))
    );
  }, [carCategories]);
  const [carCategoryIds, setCarCategoryIds] = useState<number[]>([]);
  const onChangeCarCategory = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    updateCarCategoryCheckBoxGroupValues(values);
    setCarCategoryIds(selectedValues.map((v) => Number(v)));
  };

  //車両No
  const [carNo, setCarNo] = useState(initCars.carNo);
  const onChangeCarNo = (name: string) => {
    setCarNo(name);
  };

  // 免許種別
  const { data: driverLicense } = useGetDriverLicensesQuery();
  const [
    driverLicenseCheckBoxGroupValues,
    updateDriverLicenseCheckBoxGroupValues,
  ] = useState<ValueObj[]>([]);
  useEffect(() => {
    updateDriverLicenseCheckBoxGroupValues(
      (driverLicense || []).map((item) => ({
        value: `${item.driverLicenseId}`,
        label: item.driverLicense,
        checked: false,
      }))
    );
  }, [driverLicense]);
  const [driverLicenseIds, setDriverLicenseIds] = useState<number[]>([]);
  const onChangeDriverLicense = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    updateDriverLicenseCheckBoxGroupValues(values);
    setDriverLicenseIds(selectedValues.map((v) => Number(v)));
  };

  //利用状況
  const [isCheckAvailable, setIsCheckAvailable] = useState(true);
  const [isCheckUnavailable, setIsCheckUnavailable] = useState(true);

  // 条件クリア
  const onClear = () => {
    setCarCategoryIds([]);
    setDriverLicenseIds([]);
    setCarNo('');
    updateDriverLicenseCheckBoxGroupValues(
      driverLicenseCheckBoxGroupValues.map((v) => ({ ...v, checked: false }))
    );
    setIsCheckAvailable(true);
    setIsCheckUnavailable(true);
    updateCarCategoryCheckBoxGroupValues(
      carCategoryCheckBoxGroupValues.map((v) => ({ ...v, checked: false }))
    );
    updateCarsData((prev) => ({
      ...prev,
      carCategoryIds: [],
      carNo: '',
      driverLicenseIds: [],
      isAvailable: ['available', 'unavailable'],
      page: 1,
    }));
  };

  // 検索
  const onSubmit = () => {
    const isAvailable: string[] = [];
    if (isCheckAvailable) isAvailable.push('available');
    if (isCheckUnavailable) isAvailable.push('unavailable');
    updateCarsData((prev) => ({
      ...prev,
      carCategoryIds,
      carNo,
      driverLicenseIds,
      isAvailable,
      page: 1,
    }));
  };

  // ページャー変更
  const onChangePerPage = (perPage: string) => {
    updateCarsData((prev) => ({ ...prev, page: 1, limit: Number(perPage) }));
  };
  const onPage = (page: number) => {
    updateCarsData((prev) => ({
      ...prev,
      page,
    }));
  };

  const [formData, setFormData] = useState<CarFormDataProviderProps | null>(
    null
  );

  const [createCar] = usePostCarsMutation();
  const onCreateCar = () => {
    setFormData({
      carId: null,
      type: 'create',
      onSubmit: (data) => {
        createCar(data)
          .unwrap()
          .then(() => {
            setFormData(null);
          })
          .catch(logger.error);
      },
    });
  };

  const onConditionEnter = useKeyAction(keyList.ENTER, onSubmit);

  return (
    <HeaderLayout
      title="車両情報"
      controls={
        <div className={styles.buttons}>
          <PolicyRequired policy={16}>
            <div className={styles.button}>
              <CsvDownload type={'car'} />
            </div>
            <div className={styles.button}>
              <CsvInput type={'car'} />
            </div>
          </PolicyRequired>
          <PolicyRequired policy={13}>
            <div className={styles.button}>
              <Button variants={['secondary', 'contain']} onClick={onCreateCar}>
                <FontAwesomeIcon icon={faTruckFront} className={styles.icon} />
                車両登録
              </Button>
            </div>
          </PolicyRequired>
        </div>
      }
    >
      <PolicyRequired policy={17}>
        <div className={styles.conditions} onKeyDown={onConditionEnter}>
          <div className={styles.conditionInputs}>
            <div className={styles.carCategory}>
              <CheckBoxGroup
                values={carCategoryCheckBoxGroupValues}
                onChange={onChangeCarCategory}
                placeholder="車両カテゴリ"
                styleType="carCategory"
              ></CheckBoxGroup>
            </div>
            <div className={styles.carNo}>
              <Input
                value={carNo}
                onChange={(e) => onChangeCarNo(e.target.value)}
                variants={['fit']}
                placeholder="車両No"
              ></Input>
            </div>
            <div className={styles.driverLicense}>
              <CheckBoxGroup
                values={driverLicenseCheckBoxGroupValues}
                onChange={onChangeDriverLicense}
                placeholder="免許種別"
              ></CheckBoxGroup>
            </div>
            <div className={styles.isAvailable}>
              <span>利用:</span>
              <CheckBox
                label="可"
                checked={isCheckAvailable}
                onChange={() => {
                  setIsCheckAvailable((prevState) => !prevState);
                }}
              />
              <CheckBox
                label="不可"
                checked={isCheckUnavailable}
                onChange={() => {
                  setIsCheckUnavailable((prevState) => !prevState);
                }}
              />
            </div>
          </div>
          <div className={styles.conditionButtons}>
            <div className={styles.clearButton}>
              <Button variants={['contain', 'cancel']} onClick={onClear}>
                クリア
              </Button>
            </div>
            <Button
              variants={['contain', 'secondary', 'search']}
              onClick={onSubmit}
            >
              検索
            </Button>
          </div>
        </div>
      </PolicyRequired>
      {(cars ? cars.cars : []).length > 0 ? (
        <>
          <div className={styles.pageContainer}>
            <SelectPageSize
              value={`${carsData.limit}`}
              onChange={(e) => onChangePerPage(e.target.value)}
            />
            <div>
              <Pagenation
                page={carsData.page || 1}
                onPage={onPage}
                perPage={carsData.limit || 0}
                total={cars ? cars.total : 0}
              ></Pagenation>
            </div>
          </div>
          <div className={styles.content}>
            <CarsTable
              cars={cars ? cars.cars : []}
              openEdit={setFormData}
              sidebarClose={() => {
                setFormData(null);
              }}
            />
          </div>
        </>
      ) : (
        <div className={styles.noResultFound}>
          <NoResultFound />
        </div>
      )}
      <CarFormDataProvider value={formData}>
        <EditSideBar
          close={() => {
            setFormData(null);
          }}
        />
      </CarFormDataProvider>
    </HeaderLayout>
  );
}

export default Cars;
